/**
 * Cart reserve timer: configurable countdown at top of cart drawer.
 * Starts when first item is added; persists via localStorage or sessionStorage.
 * Resets when cart is empty; optionally resets when new item added.
 * Integrates with theme cart hooks (PUB_SUB_EVENTS.cartUpdate).
 */
const CART_RESERVE_STORAGE_KEY = 'cart_reserve_end';

function formatTimeLeft(secondsLeft) {
  if (secondsLeft <= 0) return '0:00';
  const m = Math.floor(secondsLeft / 60);
  const s = Math.floor(secondsLeft % 60);
  return m + ':' + (s < 10 ? '0' : '') + s;
}

function getStorage(type) {
  try {
    return type === 'localStorage' ? localStorage : sessionStorage;
  } catch (e) {
    return sessionStorage;
  }
}

class CartReserveTimer extends HTMLElement {
  constructor() {
    super();
    this._intervalId = null;
    this._unsubscribe = null;
    this._durationMs = 150000;
    this._storage = null;
    this._lastItemCount = 0;
    this._hasExpired = false;
  }

  connectedCallback() {
    this._label = this.getAttribute('data-label') || 'Your cart is reserved for';
    this._unit = this.getAttribute('data-unit') || 'minutes';
    this._expiredLabel = this.getAttribute('data-expired-label') || 'Reservation ended';
    const minutes = parseFloat(this.getAttribute('data-duration-minutes'), 10);
    if (minutes > 0) this._durationMs = minutes * 60 * 1000;
    this._resetOnNewItem = this.getAttribute('data-reset-on-new-item') === 'true';
    this._showProgressBar = this.getAttribute('data-show-progress-bar') === 'true';
    this._showExpiredMessage = this.getAttribute('data-show-expired-message') !== 'false';
    const storageType = this.getAttribute('data-storage') || 'localStorage';
    this._storage = getStorage(storageType);

    this._labelEl = this.querySelector('[data-reserve-label]');
    this._valueEl = this.querySelector('[data-reserve-value]');
    this._fillEl = this.querySelector('[data-reserve-fill]');

    this._clearStaleStorage();

    this._tick();

    this._intervalId = setInterval(() => this._tick(), 1000);

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      this._unsubscribe = subscribe(window.PUB_SUB_EVENTS.cartUpdate, (ev) => this._onCartUpdate(ev));
    }
  }

  disconnectedCallback() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    if (this._unsubscribe && typeof this._unsubscribe === 'function') {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  _onCartUpdate(ev) {
    const cartData = ev && ev.cartData;
    const itemCount = cartData && typeof cartData.item_count === 'number' ? cartData.item_count : 0;

    if (itemCount === 0) {
      this._clearReservation();
      this._lastItemCount = 0;
      this._hasExpired = false;
      this._tick();
      return;
    }

    const hadItems = this._lastItemCount > 0;
    const shouldSetTimer =
      !hadItems ||
      this._resetOnNewItem ||
      itemCount > this._lastItemCount;
    this._lastItemCount = itemCount;

    if (shouldSetTimer) {
      try {
        this._hasExpired = false;
        this._storage.setItem(CART_RESERVE_STORAGE_KEY, String(Date.now() + this._durationMs));
      } catch (e) {}
    }

    this._tick();
  }

  _clearReservation() {
    try {
      this._storage.removeItem(CART_RESERVE_STORAGE_KEY);
    } catch (e) {}
  }

  _clearStaleStorage() {
    try {
      const raw = this._storage.getItem(CART_RESERVE_STORAGE_KEY);
      if (raw) {
        const endAt = parseInt(raw, 10);
        if (endAt <= Date.now()) this._storage.removeItem(CART_RESERVE_STORAGE_KEY);
      }
    } catch (e) {}
  }

  _tick() {
    let endAt = null;
    try {
      const raw = this._storage.getItem(CART_RESERVE_STORAGE_KEY);
      if (raw) endAt = parseInt(raw, 10);
    } catch (e) {}

    const now = Date.now();

    if (!endAt || endAt <= now) {
      if (!endAt && this._durationMs > 0 && !this._hasExpired) {
        endAt = now + this._durationMs;
        try {
          this._storage.setItem(CART_RESERVE_STORAGE_KEY, String(endAt));
        } catch (e) {}
      }
    }

    try {
      const raw = this._storage.getItem(CART_RESERVE_STORAGE_KEY);
      if (raw) endAt = parseInt(raw, 10);
    } catch (e) {}

    const valueEl = this._valueEl;
    const labelEl = this._labelEl;
    const fillEl = this._fillEl;

    if (!endAt || endAt <= now) {
      if (endAt && endAt <= now) {
        this._hasExpired = true;
        try {
          this._storage.removeItem(CART_RESERVE_STORAGE_KEY);
        } catch (e) {}
      }
      if (this._hasExpired) {
        this.setAttribute('data-state', 'expired');
        if (valueEl) valueEl.textContent = this._showExpiredMessage ? this._expiredLabel : '';
        if (labelEl) labelEl.textContent = this._showExpiredMessage ? '' : this._label + ' ';
        if (fillEl) fillEl.style.setProperty('--reserve-progress', '0');
        const track = this.querySelector('[role="progressbar"]');
        if (track) track.setAttribute('aria-valuenow', '0');
      } else {
        this.removeAttribute('data-state');
        const totalSec = Math.ceil(this._durationMs / 1000);
        if (valueEl) valueEl.textContent = formatTimeLeft(totalSec) + ' ' + this._unit;
        if (labelEl) labelEl.textContent = this._label + ' ';
        if (fillEl) fillEl.style.setProperty('--reserve-progress', '100');
        const track = this.querySelector('[role="progressbar"]');
        if (track) track.setAttribute('aria-valuenow', '100');
      }
      return;
    }
    this._hasExpired = false;

    this.removeAttribute('data-state');
    const secondsLeft = Math.ceil((endAt - now) / 1000);
    const totalSec = Math.ceil(this._durationMs / 1000);
    if (valueEl) valueEl.textContent = formatTimeLeft(secondsLeft) + ' ' + this._unit;
    if (labelEl) labelEl.textContent = this._label + ' ';

    if (this._showProgressBar && fillEl && totalSec > 0) {
      const progress = Math.max(0, Math.min(100, (secondsLeft / totalSec) * 100));
      fillEl.style.setProperty('--reserve-progress', String(progress));
      const track = this.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(Math.round(progress)));
    }
  }
}

customElements.define('cart-reserve-timer', CartReserveTimer);
